import { Routes } from '@angular/router';
import { authGuard } from './auth.guard';
import { canActivateChildGuard } from './can-activate-child.guard';

export const routes: Routes = [
  {
    path: '',
    loadComponent: () =>
      import('./pages/home/home.component').then((m) => m.HomeComponent),
    canActivate: [canActivateChildGuard],
  },
  {
    path: 'generate/:any',
    loadComponent: () =>
      import('./pages/features/features.component').then(
        (m) => m.FeaturesComponent,
      ),
    data: { selfMeta: true },
  },
  {
    path: 'login',
    loadComponent: () =>
      import('./pages/login/login.component').then((m) => m.LoginComponent),
    data: {
      preload: true,
      meta: {
        title: 'Log In',
        description:
          'Log in to your account to create realistic AI images and videos, chat with AI characters, and manage your gallery.',
      },
    },
  },
  {
    path: 'register',
    loadComponent: () =>
      import('./pages/register/register.component').then(
        (m) => m.RegisterComponent,
      ),
    data: {
      preload: true,
      meta: {
        title: 'Create Your Free Account',
        description:
          'Sign up free and start generating realistic AI images and videos in seconds. Design AI characters and influencers — no credit card required.',
      },
    },
  },
  {
    path: 'create',
    canActivate: [authGuard, canActivateChildGuard],
    loadComponent: () =>
      import('./pages/create-avatar/create-avatar.component').then(
        (m) => m.CreateAvatarComponent,
      ),
  },
  {
    path: 'train/:promptId',
    canActivate: [authGuard, canActivateChildGuard],
    loadComponent: () =>
      import(
        './pages/create-character-training/create-character-training.component'
      ).then((m) => m.CreateCharacterTrainingComponent),
  },
  {
    path: 'train-user',
    canActivate: [authGuard, canActivateChildGuard],
    loadComponent: () =>
      import('./pages/train-user/train-user.component').then(
        (m) => m.TrainUserComponent,
      ),
  },
  {
    path: 'train',
    canActivate: [authGuard, canActivateChildGuard],
    loadComponent: () =>
      import('./pages/training-status/training-status.component').then(
        (m) => m.TrainingStatusComponent,
      ),
  },
  {
    path: 'create/:characterRoute',
    canActivate: [authGuard, canActivateChildGuard],
    loadComponent: () =>
      import('./pages/create-avatar/create-avatar.component').then(
        (m) => m.CreateAvatarComponent,
      ),
  },
  {
    path: 'share/:shareSlug',
    loadComponent: () =>
      import('./pages/share-preview/share-preview.component').then(
        (m) => m.SharePreviewComponent,
      ),
    // The component sets per-share title/description/OG/JSON-LD itself.
    data: { selfMeta: true },
  },
  {
    // Full editor experience for a shared creation (pre-Phase-4 behavior
    // of /share/:shareSlug). Client-rendered; reached via the preview page
    // or by automatic redirect for authenticated users.
    path: 'share/:shareSlug/edit',
    loadComponent: () =>
      import('./pages/create-avatar/create-avatar.component').then(
        (m) => m.CreateAvatarComponent,
      ),
  },
  {
    path: 'gallery',
    canActivate: [canActivateChildGuard],
    loadComponent: () =>
      import('./pages/gallery/gallery.component').then(
        (m) => m.GalleryComponent,
      ),
  },
  {
    path: 'gallery/item/:promptId',
    canActivate: [canActivateChildGuard],
    loadComponent: () =>
      import('./pages/gallery-post/gallery-post.component').then(
        (m) => m.GalleryPostComponent,
      ),
  },
  {
    path: 'payment',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/payment/payment.component').then((m) => m.default),
  },
  {
    path: 'server',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/server/server.component').then((m) => m.ServerComponent),
  },
  {
    path: 'server/purchase',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/server-purchase/server-purchase.component').then(
        (m) => m.ServerPurchaseComponent,
      ),
  },
  {
    path: 'server/purchase-with-credits',
    canActivate: [authGuard],
    loadComponent: () =>
      import(
        './pages/server-purchase-with-credits/server-purchase-with-credits.component'
      ).then((m) => m.ServerPurchaseWithCreditsComponent),
  },
  {
    path: 'confirm-email',
    loadComponent: () =>
      import('./pages/confirm-email/confirm-email.component').then(
        (m) => m.ConfirmEmailComponent,
      ),
  },
  {
    path: 'terms',
    loadComponent: () =>
      import('./pages/terms/terms.component').then((m) => m.TermsComponent),
    data: {
      meta: {
        title: 'Terms of Service',
        description:
          'Terms of service for using the AI image and video generation platform.',
      },
    },
  },
  {
    path: 'policy',
    loadComponent: () =>
      import('./pages/terms-new/terms-new.component').then(
        (m) => m.TermsNewComponent,
      ),
    data: {
      meta: {
        title: 'Policies',
        description:
          'Content policies, privacy policy, and legal information for the AI image and video generation platform.',
      },
    },
  },
  {
    path: 'region-blocked',
    loadComponent: () =>
      import('./pages/region-blocked/region-blocked.component').then(
        (m) => m.RegionBlockedComponent,
      ),
  },
  {
    path: 'usc-2257-exemption',
    loadComponent: () =>
      import('./pages/usc2257/usc2257.component').then(
        (m) => m.Usc2257Component,
      ),
    data: {
      meta: {
        title: '18 U.S.C. § 2257 Exemption Statement',
        description:
          '18 U.S.C. § 2257 exemption statement for AI-generated content.',
      },
    },
  },
  {
    path: 'reset-password',
    loadComponent: () =>
      import('./pages/reset-password/reset-password.component').then(
        (m) => m.ResetPasswordComponent,
      ),
    data: {
      meta: {
        title: 'Reset Password',
        description: 'Reset the password for your account.',
      },
    },
  },
  {
    path: 'profile/create',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/create-profile/create-profile.component').then(
        (m) => m.CreateProfileComponent,
      ),
  },
  {
    path: 'profile/import',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/import-profile/import-profile.component').then(
        (m) => m.ImportProfileComponent,
      ),
  },
  {
    path: 'profile/:userSlug',
    canActivate: [authGuard, canActivateChildGuard],
    loadComponent: () =>
      import('./pages/profile/profile.component').then(
        (m) => m.ProfileComponent,
      ),
  },
  {
    path: 'profiles',
    canActivate: [authGuard, canActivateChildGuard],
    loadComponent: () =>
      import('./pages/profile-browse/profile-browse.component').then(
        (m) => m.ProfileBrowseComponent,
      ),
  },
  {
    path: 'posts',
    canActivate: [canActivateChildGuard],
    loadComponent: () =>
      import('./pages/posts/posts.component').then((m) => m.PostsComponent),
    data: {
      meta: {
        title: 'Community Posts — AI Images & Videos',
        description:
          'Browse the latest AI-generated images and videos shared by the community. Realistic, anime, and creative styles — updated daily.',
      },
    },
  },
  {
    path: 'posts/:postId',
    canActivate: [],
    loadComponent: () =>
      import('./pages/post/post.component').then((m) => m.PostComponent),
    // The component sets per-post title/description/JSON-LD itself.
    data: { selfMeta: true },
  },
  {
    path: 'faq',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/faq/faq.component').then((m) => m.FaqComponent),
  },
  {
    path: 'settings',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/settings/settings.component').then(
        (m) => m.SettingsComponent,
      ),
  },
  {
    path: 'admin',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/admin/server-table/server-table.component').then(
        (m) => m.ServerTableComponent,
      ),
  },
  {
    path: 'admin/loras',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/admin/admin-templates/admin-templates.component').then(
        (m) => m.AdminTemplatesComponent,
      ),
  },
  {
    path: 'admin/payments',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/admin/admin-payments/admin-payments.component').then(
        (m) => m.AdminPaymentsComponent,
      ),
  },
  {
    path: 'uncensored-ai',
    loadComponent: () =>
      import('./pages/blog/blog.component').then((m) => m.BlogComponent),
    data: { selfMeta: true },
  },
  {
    path: 'leaderboard',
    canActivate: [authGuard, canActivateChildGuard],
    loadComponent: () =>
      import('./pages/leaderboard/leaderboard.component').then(
        (m) => m.LeaderboardComponent,
      ),
  },
  {
    // Public: the challenge brief is a landing page. Entries stay behind
    // login (the entries endpoint requires auth) — see ChallengeComponent.
    path: 'challenges',
    canActivate: [canActivateChildGuard],
    loadComponent: () =>
      import('./pages/challenge/challenge.component').then(
        (m) => m.ChallengeComponent,
      ),
    // The component sets per-challenge title/description/OG/JSON-LD itself.
    data: { selfMeta: true },
  },
  {
    path: 'challenges/all',
    canActivate: [canActivateChildGuard],
    loadComponent: () =>
      import('./pages/challenge-browse/challenge-browse.component').then(
        (m) => m.ChallengeBrowseComponent,
      ),
    data: {
      meta: {
        title: 'Past AI Art Challenges',
        description:
          'Browse every past weekly AI art challenge — themed AI image and video contests with 250 free credits for the winning entry.',
      },
    },
  },
  {
    path: 'templates',
    canActivate: [authGuard, canActivateChildGuard],
    loadComponent: () =>
      import('./pages/template/template.component').then(
        (m) => m.TemplateComponent,
      ),
  },
  {
    path: 'templates-learn',
    canActivate: [authGuard, canActivateChildGuard],
    loadComponent: () =>
      import('./pages/template-learn/template-learn.component').then(
        (m) => m.TemplateLearnComponent,
      ),
  },
  {
    path: 'coupon',
    canActivate: [authGuard, canActivateChildGuard],
    loadComponent: () =>
      import('./pages/coupon/coupon.component').then((m) => m.CouponComponent),
  },
  {
    path: 'invoices',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/invoices/invoices.component').then(
        (m) => m.InvoicesComponent,
      ),
  },
  {
    path: 'verify',
    canActivate: [authGuard, canActivateChildGuard],
    loadComponent: () =>
      import('./pages/verify-account/verify-account.component').then(
        (m) => m.VerifyAccountComponent,
      ),
  },
  {
    path: 'updates',
    loadComponent: () =>
      import('./pages/updates/updates.component').then(
        (m) => m.UpdatesComponent,
      ),
    data: {
      meta: {
        title: "What's New — Product Updates",
        description:
          'Latest features, AI model releases, and improvements to the AI image and video generator.',
      },
    },
  },
  {
    path: 'ai-model-overview',
    loadComponent: () =>
      import('./pages/model-overview/model-overview.component').then(
        (m) => m.ModelOverviewComponent,
      ),
    data: { selfMeta: true },
  },
  {
    path: 'admin-gallery',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/admin/prompts/prompts.component').then(
        (m) => m.PromptsComponent,
      ),
  },
  {
    path: 'admin/moderation-queue',
    canActivate: [authGuard],
    loadComponent: () =>
      import(
        './pages/admin/admin-moderation-queue/admin-moderation-queue.component'
      ).then((m) => m.AdminModerationQueueComponent),
  },
  {
    path: 'admin/user-server',
    canActivate: [authGuard],
    loadComponent: () =>
      import(
        './pages/admin/user-server-table/user-server-table.component'
      ).then((m) => m.UserServerTableComponent),
  },
  {
    path: 'partner-program',
    loadComponent: () =>
      import('./pages/partner-program/partner-program.component').then(
        (m) => m.PartnerProgramComponent,
      ),
    data: {
      meta: {
        title: 'Partner & Affiliate Program',
        description:
          'Earn recurring commissions by promoting the AI image and video generation platform. Join the partner program for free.',
      },
    },
  },
  {
    path: 'free-gift',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/free-gift/free-gift.component').then(
        (m) => m.FreeGiftComponent,
      ),
  },
  {
    path: 'tutorial',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/tutorial/tutorial.component').then(
        (m) => m.TutorialComponent,
      ),
  },
  {
    path: 'chats',
    canActivate: [authGuard, canActivateChildGuard],
    loadComponent: () =>
      import('./pages/chats/chats.component').then((m) => m.ChatsComponent),
  },
  {
    path: 'chats/:chatId',
    canActivate: [authGuard, canActivateChildGuard],
    loadComponent: () =>
      import('./pages/chat/chat.component').then((m) => m.ChatComponent),
  },
  {
    path: 'chat',
    canActivate: [authGuard, canActivateChildGuard],
    loadComponent: () =>
      import('./pages/chat/chat.component').then((m) => m.ChatComponent),
  },
  {
    path: 'create-chat-character',
    canActivate: [authGuard, canActivateChildGuard],
    loadComponent: () =>
      import(
        './pages/create-chat-character/create-chat-character.component'
      ).then((m) => m.CreateChatCharacterComponent),
  },
  {
    path: 'chats/characters/:id/edit',
    canActivate: [authGuard, canActivateChildGuard],
    loadComponent: () =>
      import('./pages/edit-chat-character/edit-chat-character.component').then(
        (m) => m.EditChatCharacterComponent,
      ),
  },
];
